import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  offerPageUrl, privacyPageUrl, refundPageUrl, faqPageUrl, pricingPageUrl, termsPageUrl, paymentPageUrl, editorPageUrl, recognizePageUrl, aiPageUrl, fontCreatorPageUrl,
} from '../../../helpers/urls';
import logoImg from '../header/logo.svg';

export const FooterComponent: FC = () => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  return (
    <footer className="bg-[#1B1C38] px-4 py-16 relative z-[61]">
      <div className="max-w-5xl mx-auto">

        {/* 3 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-8">
          <div>
            <p className="text-xs font-semibold text-white/40 uppercase tracking-wide mb-3">{t('footer.tools')}</p>
            <nav className="flex flex-col gap-2">
              <a href={editorPageUrl} className="text-sm text-white/70 hover:text-white transition-colors">{t('footer.noteGenerator')}</a>
              <a href={recognizePageUrl} className="text-sm text-white/70 hover:text-white transition-colors">{t('footer.recognition')}</a>
              <a href={aiPageUrl} className="text-sm text-white/70 hover:text-white transition-colors">{t('sidebar.aiChat')}</a>
              <a href={fontCreatorPageUrl} className="text-sm text-white/70 hover:text-white transition-colors">{t('footer.fontGenerator')}</a>
            </nav>
          </div>

          <div>
            <p className="text-xs font-semibold text-white/40 uppercase tracking-wide mb-3">{t('footer.documents')}</p>
            <nav className="flex flex-col gap-2">
              {isEn
                ? <a href={termsPageUrl} className="text-sm text-white/70 hover:text-white transition-colors">{t('sidebar.terms')}</a>
                : <a href={offerPageUrl} className="text-sm text-white/70 hover:text-white transition-colors">{t('sidebar.offer')}</a>
              }
              <a href={privacyPageUrl} className="text-sm text-white/70 hover:text-white transition-colors">{t('sidebar.privacy')}</a>
              <a href={refundPageUrl} className="text-sm text-white/70 hover:text-white transition-colors">{t('sidebar.refund')}</a>
              <a href={pricingPageUrl} className="text-sm text-white/70 hover:text-white transition-colors">{t('sidebar.pricing')}</a>
              <a href={faqPageUrl} className="text-sm text-white/70 hover:text-white transition-colors">{t('sidebar.faq')}</a>
              <a href={paymentPageUrl} className="text-sm text-white/70 hover:text-white transition-colors">{t('footer.payment')}</a>
            </nav>
          </div>

          <div>
            <p className="text-xs font-semibold text-white/40 uppercase tracking-wide mb-3">{t('footer.contacts')}</p>
            <a href="mailto:pisaka.online@gmail.com" className="block text-sm text-white/70 hover:text-white transition-colors">pisaka.online@gmail.com</a>
            <a href="tel:+77058218491" className="block text-sm text-white/70 hover:text-white transition-colors mt-2">+7 (705) 821-84-91</a>
          </div>
        </div>

        {/* Bottom: logo + copyright + socials */}
        <div className="border-t border-white/10 pt-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-2.5">
            <img src={logoImg} alt="Pisaka" className="w-7 h-7" />
            <span className="text-white/80 font-bold">{t('brand')}</span>
            <span className="text-white/30 text-xs ml-1">© {new Date().getFullYear()} pisaka.online</span>
          </a>
          <div className="flex gap-3">
            <a href="https://vk.com/pisaka.online" target="_blank" rel="noreferrer" className="text-white/40 hover:text-white transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M15.07 2H8.93C3.33 2 2 3.33 2 8.93v6.14C2 20.67 3.33 22 8.93 22h6.14C20.67 22 22 20.67 22 15.07V8.93C22 3.33 20.67 2 15.07 2zm3.08 13.96h-1.6c-.6 0-.79-.48-1.87-1.57-1-.93-1.43-1.06-1.67-1.06-.34 0-.44.1-.44.58v1.43c0 .41-.13.66-1.22.66-1.8 0-3.8-1.09-5.2-3.13C5.1 10.48 4.7 8.8 4.7 8.43c0-.24.1-.47.58-.47h1.6c.43 0 .6.2.76.66.84 2.43 2.24 4.56 2.82 4.56.22 0 .32-.1.32-.65V10.1c-.07-1.17-.68-1.27-.68-1.69 0-.2.17-.4.44-.4h2.52c.37 0 .5.2.5.63v3.4c0 .37.17.5.27.5.22 0 .4-.13.8-.54 1.23-1.38 2.1-3.5 2.1-3.5.12-.24.3-.46.73-.46h1.6c.48 0 .59.25.48.63-.2.94-2.15 3.68-2.15 3.68-.17.28-.24.4 0 .71.17.24.73.73 1.1 1.17.68.78 1.2 1.43 1.34 1.88.14.44-.08.67-.53.67z"/></svg>
            </a>
            <a href="https://t.me/pisaka_online" target="_blank" rel="noreferrer" className="text-white/40 hover:text-white transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8-1.68 7.92c-.12.56-.46.7-.93.43l-2.57-1.9-1.24 1.2c-.14.13-.25.25-.51.25l.18-2.6 4.7-4.25c.2-.18-.05-.28-.32-.1L7.9 14.67l-2.52-.79c-.55-.17-.56-.55.12-.81l9.85-3.8c.46-.17.86.11.29.53z"/></svg>
            </a>
          </div>
        </div>

      </div>
    </footer>
  );
};
